'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';

import { useRouter } from 'next/navigation';

import { authApi } from '@/lib/api';
import { ApiError } from '@/lib/api-client';

import type { UserInToken, UserRole } from '@/lib/types';

const ROLE_HOME: Record<string, string> = {
  soc: '/soc/dashboard',
  soc_admin: '/soc/dashboard',
  client: '/client/dashboard',
  customer: '/customer/dashboard',
};

const WEB_ALLOWED_ROLES: UserRole[] = [
  'soc',
  'soc_admin',
  'client',
  'customer',
];

interface AuthContextValue {
  user: UserInToken | null;
  isLoading: boolean;
  isSoc: boolean;
  isSocAdmin: boolean;
  isClient: boolean;
  isCustomer: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  homeRoute: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();

  const [user, setUser] = useState<UserInToken | null>(null);
  const [isLoading, setLoading] = useState(true);

  const isSocAdmin = user?.role === 'soc_admin';
  const isSoc = user?.role === 'soc' || isSocAdmin;
  const isClient = user?.role === 'client';
  const isCustomer = user?.role === 'customer';

  const homeRoute = user
    ? ROLE_HOME[user.role] ?? '/login'
    : '/login';

  // Validate session on app boot — cookie is httpOnly, so we can't check
  // for its presence in JS; just ask the backend.
 useEffect(() => {
  authApi
    .me()
    .then((me) => {
      if (!WEB_ALLOWED_ROLES.includes(me.role)) {
        setUser(null);
        return;
      }
      setUser(me);
    })
    .catch((e) => {
      setUser(null);
      if (
        e instanceof ApiError &&
        e.status === 403 &&
        typeof window !== 'undefined' &&
        window.location.pathname !== '/login'
      ) {
        // Clear the still-valid-looking JWT cookie server-side first —
        // otherwise middleware decodes a valid role from it and bounces
        // /login straight back to roleHome(), causing a redirect loop.
        authApi.logout().finally(() => {
          window.location.href = '/login?reason=deactivated';
        });
      }
    })
    .finally(() => {
      setLoading(false);
    });
}, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const me = await authApi.login({
        email,
        password,
      });

      if (!WEB_ALLOWED_ROLES.includes(me.role)) {
        await authApi.logout();
        throw new Error(
          'This web portal is only available for SOC, client, and customer users.'
        );
      }

      setUser(me);

      await new Promise((resolve) =>
        setTimeout(resolve, 50)
      );

      if (me.must_change_password) {
        router.push('/change-password');
        return;
      }

      router.push(
        ROLE_HOME[me.role] ?? '/dashboard'
      );
    },
    [router]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      router.push('/login');
    }
  }, [router]);

  const contextValue = useMemo(
    () => ({
      user,
      isLoading,
      isSoc,
      isSocAdmin,
      isClient,
      isCustomer,
      homeRoute,
      login,
      logout,
    }),
    [
      user,
      isLoading,
      isSoc,
      isSocAdmin,
      isClient,
      isCustomer,
      homeRoute,
      login,
      logout,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error(
      'useAuth must be used inside <AuthProvider>'
    );
  }

  return ctx;
}